"use client";

import { Switch as SwitchPrimitive } from "radix-ui";
import type * as React from "react";

import { cn } from "../lib/cn";

/**
 * The track reads as a tinted well rather than a filled bar: off is the raised
 * surface, on is the accent at low opacity with a matching hairline, and the
 * thumb carries the colour. That is Qcom's operator-console switch, kept as the
 * one shared shape so a console and an analytics screen do not disagree about
 * what "on" looks like.
 *
 * Geometry is stated rather than derived: a 38x21 track with a 1px hairline and
 * 2px inset leaves the 15px thumb exactly 17px of travel.
 */
function Switch({
  className,
  ...props
}: React.ComponentProps<typeof SwitchPrimitive.Root>) {
  return (
    <SwitchPrimitive.Root
      data-slot="switch"
      className={cn(
        "peer inline-flex h-[21px] w-[38px] shrink-0 cursor-pointer items-center rounded-full border px-[2px] transition-colors",
        "border-border bg-muted",
        "data-[state=checked]:border-primary/28 data-[state=checked]:bg-primary/10",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        "disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb
        data-slot="switch-thumb"
        className={cn(
          "pointer-events-none block size-[15px] rounded-full ring-0 transition-[transform,background-color,box-shadow] duration-150",
          "bg-muted-foreground/72 data-[state=unchecked]:translate-x-0",
          "data-[state=checked]:translate-x-[17px] data-[state=checked]:bg-primary",
          "data-[state=checked]:shadow-[0_0_10px] data-[state=checked]:shadow-primary",
        )}
      />
    </SwitchPrimitive.Root>
  );
}

export { Switch };
